import { apiClient } from '@/utils/api-client';
import type { AppSettings } from '@/types/api';

export interface ProductFeature {
  id: number;
  name: string;
  code: string;
}

export type FeatureMode = 'all_users' | 'disabled' | 'per_user';

export interface AccountSettings {
  id: string;
  company_name: string;
  company_logo_url?: string;
  registration_link?: string;
  uses_saml_login: boolean;
  enabled_features?: ProductFeature[];
  proxima_api_mode?: FeatureMode;
  kaiya_memory_mode?: FeatureMode;
  kaiya_memory_available?: boolean;
  kaiya_mcp_mode?: FeatureMode;
  kaiya_public_links_mode?: FeatureMode;
  guided_use_cases_mode?: FeatureMode;
  guided_use_cases_available?: boolean;
}

export interface AppSettingsAffectedUsers {
  proxima_enabled: number;
  pathways_enabled: number;
  community_enabled: number;
  kaiya_enabled: number;
}

export interface License {
  id: string;
  name: string;
  product_id: string;
  expiration_date: string;
  total_seats: number;
  used_seats: number;
  seats_purchased: number;
  seats_comped: number;
  auto_assigned: boolean;
  features?: string[];
}

export interface LicenseAddon {
  id: string;
  name: string;
  type: string;
  expiration_date: string;
  total_seats: number;
  used_seats: number;
}

export interface AccountUsage {
  licenses: License[];
  addons: LicenseAddon[];
}

export interface UploadUrlResponse {
  upload_url: string;
  file_key: string;
}

class AccountSettingsService {
  /**
   * Get account settings for the current license admin's organization
   */
  async getSettings(accountId: string): Promise<AccountSettings> {
    const response = await apiClient.get<{ data: { attributes: AccountSettings } }>(`/api/accounts/${accountId}/settings`);
    return response.data.attributes;
  }

  /**
   * Update account settings
   */
  async updateSettings(accountId: string, settings: Partial<AccountSettings>): Promise<AccountSettings> {
    const response = await apiClient.patch<{ data: { attributes: AccountSettings } }>(`/api/accounts/${accountId}/settings`, {
      data: {
        type: 'account_settings',
        attributes: settings
      }
    });
    return response.data.attributes;
  }

  /**
   * Get app level settings for membership products
   */
  async getAppSettings(accountId: string): Promise<AppSettings> {
    const response = await apiClient.get<{ data: { attributes: AppSettings } }>(`/api/accounts/${accountId}/app_settings`);
    return response.data.attributes;
  }

  /**
   * Update app level settings for membership products
   */
  async updateAppSettings(accountId: string, settings: Partial<AppSettings>): Promise<AppSettings> {
    const response = await apiClient.patch<{ data: { attributes: AppSettings } }>(`/api/accounts/${accountId}/app_settings`, {
      data: {
        type: 'app_settings',
        attributes: settings
      }
    });
    return response.data.attributes;
  }

  /**
   * Get affected users count for app level settings
   */
  async getAppSettingsAffectedUsers(accountId: string): Promise<AppSettingsAffectedUsers> {
    const response = await apiClient.get<{ data: { attributes: AppSettingsAffectedUsers } }>(`/api/accounts/${accountId}/app_settings/affected_users`);
    return response.data.attributes;
  }

  /**
   * Get a presigned URL for uploading company logo
   */
  async getLogoUploadUrl(accountId: string, filename: string): Promise<UploadUrlResponse> {
    const response = await apiClient.post<{ data: UploadUrlResponse }>(`/api/accounts/${accountId}/logo-upload-url`, {
      filename
    });
    return response.data;
  }

  /**
   * Upload logo to S3 using presigned URL
   */
  async uploadLogo(uploadUrl: string, file: File): Promise<void> {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
  }

  /**
   * Confirm logo upload and update account settings
   */
  async confirmLogoUpload(accountId: string, fileKey: string): Promise<AccountSettings> {
    const response = await apiClient.post<{ data: { attributes: AccountSettings } }>(`/api/accounts/${accountId}/confirm-logo-upload`, {
      file_key: fileKey
    });
    return response.data.attributes;
  }

  /**
   * Get account usage and billing information
   */
  async getAccountUsage(accountId: string): Promise<AccountUsage> {
    const response = await apiClient.get<{ data: AccountUsage }>(`/api/accounts/${accountId}/usage`);
    // The API returns the data wrapped in a 'data' property
    return response.data || response;
  }

  /**
   * Enable a feature for the account
   */
  async enableFeature(accountId: string, featureCode: string): Promise<void> {
    await apiClient.patch(`/api/accounts/${accountId}/features/${featureCode}/enable`, {});
  }

  /**
   * Disable a feature for the account
   */
  async disableFeature(accountId: string, featureCode: string): Promise<void> {
    await apiClient.patch(`/api/accounts/${accountId}/features/${featureCode}/disable`, {});
  }

  /**
   * Set the mode for a feature (all_users, disabled, per_user)
   */
  async setFeatureMode(accountId: string, featureCode: string, mode: FeatureMode): Promise<void> {
    await apiClient.patch(`/api/accounts/${accountId}/features/${featureCode}/mode`, { mode });
  }

  /**
   * Get count of active public links for the account
   */
  async getPublicLinksCount(accountId: string): Promise<number> {
    const response = await apiClient.get<{ data: { attributes: { count: number } } }>(
      `/api/accounts/${accountId}/public_links_count`
    );
    return response.data.attributes.count;
  }

  /**
   * Get project IDs where account users are owners
   */
  async getOwnerProjectIds(accountId: string): Promise<number[]> {
    const response = await apiClient.get<{ project_ids: number[] }>(
      `/api/accounts/${accountId}/owner_project_ids`
    );
    return response.project_ids;
  }

  async getOwnerProjects(accountId: string): Promise<{ id: number; name: string }[]> {
    const response = await apiClient.get<{ projects: { id: number; name: string }[] }>(
      `/api/accounts/${accountId}/owner_projects`
    );
    return response.projects;
  }

  /**
   * Export audit log
   */
  async exportAuditLog(accountId: string): Promise<Blob> {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/accounts/${accountId}/audit-log/export`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to export audit log');
    }

    return response.blob();
  }
}

export const accountSettingsService = new AccountSettingsService();